import { useState, type ReactNode } from 'react'
import { Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { faFloppyDisk, faMinus, faPlus } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionIcon } from '../../Common/ActionIcon'
import { Button } from '../../Common/Button'
import type { FieldStatus } from '../../../hooks/usePresentPositionEdit'

export interface EditableKeyDataRowProps {
  label: string
  value: string | null
  isDirty: boolean
  // Dirty *and* valid — the editor owns the reason and shows it, this only gates Save.
  canSave: boolean
  status: FieldStatus
  message: string | null
  // When set, the row cannot be expanded and the reason is shown on hover (MSD-538).
  disabledReason?: string
  children: ReactNode
  onSave: () => void
}

function getDisplayValue(value: string | null): { text: string; color?: string; italic?: boolean } {
  if (value == null) return { text: 'No Record', color: 'brand.7', italic: true }
  if (value === '') return { text: 'N/A', color: 'brand.7' }
  return { text: value }
}

function getMessageColor(status: FieldStatus): string | undefined {
  if (status === 'success') return 'success.6'
  if (status === 'error') return 'danger.6'
  return undefined
}

export function EditableKeyDataRow({
  label,
  value,
  isDirty,
  canSave,
  status,
  message,
  disabledReason,
  children,
  onSave,
}: Readonly<EditableKeyDataRowProps>) {
  const [expanded, setExpanded] = useState(false)
  const display = getDisplayValue(value)
  const saving = status === 'saving'

  if (!expanded) {
    // data-disabled instead of disabled so the explanatory tooltip still fires.
    const editIcon = (
      <ActionIcon
        icon={faPlus}
        iconSize="sm"
        aria-label={`Edit ${label}`}
        aria-disabled={Boolean(disabledReason)}
        data-disabled={disabledReason ? true : undefined}
        onClick={() => {
          if (!disabledReason) setExpanded(true)
        }}
        style={{ flexShrink: 0 }}
      />
    )

    return (
      <Paper
        withBorder
        p="xs"
        shadow="xs"
        style={isDirty ? { backgroundColor: 'var(--mantine-color-warning-0)' } : undefined}
      >
        {/* preventGrowOverflow is off so the value takes the width the label and icon
            leave behind instead of a fixed share of the row; the label keeps its own
            width and the value truncates into what remains. */}
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs" preventGrowOverflow={false}>
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Text fz="xs" fw="var(--mantine-font-weight-semibold)" style={{ whiteSpace: 'nowrap' }}>
              {label}:
            </Text>
            {isDirty && (
              <Text fz="xs" c="warning.6" fs="italic">
                Unsaved
              </Text>
            )}
          </Group>
          <Text
            fz="xs"
            truncate
            ta="right"
            c={display.color}
            fs={display.italic ? 'italic' : undefined}
            title={display.text}
            style={{ flex: 1, minWidth: 0 }}
          >
            {display.text}
          </Text>
          {disabledReason ? (
            <Tooltip label={disabledReason} withArrow multiline maw={220}>
              {editIcon}
            </Tooltip>
          ) : (
            editIcon
          )}
        </Group>
      </Paper>
    )
  }

  return (
    <Paper withBorder p="xs" shadow="xs">
      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          {/* Same size/weight as the collapsed row and the read-only rows so expanding
              does not change the label's type scale. */}
          <Text fz="xs" fw="var(--mantine-font-weight-semibold)" style={{ whiteSpace: 'nowrap' }}>
            {label}:
          </Text>
          <Group gap={4} wrap="nowrap">
            <Button
              label="Save"
              size="xs"
              variant="filled"
              loading={saving}
              disabled={!canSave && !saving}
              onClick={onSave}
              rightSection={<FontAwesomeIcon icon={faFloppyDisk} />}
            />
            <ActionIcon
              icon={faMinus}
              iconSize="sm"
              aria-label={`Collapse ${label}`}
              onClick={() => setExpanded(false)}
            />
          </Group>
        </Group>
        {children}
        {message && (
          <Text fz="xs" c={getMessageColor(status)}>
            {message}
          </Text>
        )}
      </Stack>
    </Paper>
  )
}
